import { z } from 'zod';
import {
  REALTIME_PROVIDERS,
  e164Schema,
  inputFilterModeSchema,
  stepPlanSchema,
} from '@vagusx/shared';

/**
 * Single source of truth for the `/api/browser-call/test` request body.
 *
 * Mirrored on the API (Hono `zValidator`) and the FE (`useTestCall` hook
 * + `BrowserCallClient.startTest`) so adding a new variant or field is a
 * one-place change. Without this, the FE and API discriminated unions
 * silently drift — exactly what bit us when `locationId` was added on
 * the API side without ever reaching the FE.
 *
 * `idempotencyKey` is intentionally NOT in the body — it travels in the
 * `Idempotency-Key` HTTP header. The FE hook accepts it alongside the
 * body and sets the header; the API route reads the header and HMACs
 * it against the org id. Putting it in both places would invite a
 * future contributor to set just one and wonder why it didn't work.
 */

/**
 * Per-call override for the realtime (speech-in/speech-out) stack
 * selection. Honored only when the LiveKit session runs the realtime
 * path. Realtime providers don't always map to text providers
 * (deepgram realtime ≠ deepgram STT, xai grok voice is its own SKU).
 */
const realtimeProviderField = REALTIME_PROVIDERS.nullable().optional();

/**
 * Per-call override for the realtime model id within the chosen
 * provider. Free-form string because each provider has its own model
 * catalog (and adds/removes models faster than schema updates can
 * keep up). When omitted, the LiveKit worker falls back to its
 * per-provider default in `REALTIME_DEFAULTS`. Honored only on the
 * realtime path; ignored on pipelined.
 */
const realtimeModelField = z.string().min(1).nullable().optional();

const commonCallFields = {
  locationId: z.string().uuid().optional(),
  /**
   * Operator-selected provider the appointment is for. When set (with
   * `locationId`), the API warms that provider's open slots before the call
   * and hard-locks the agent's slot search to this provider — the agent
   * never picks or proposes a different one.
   */
  providerId: z.string().uuid().optional(),
  /**
   * Operator-selected appointment type. Drives the booking duration the
   * agent commits with. With Office Ally's type-agnostic slot scrape it does
   * NOT filter which slots are offered (those are filtered by provider).
   */
  appointmentTypeId: z.string().uuid().optional(),
  preVerified: z.boolean().default(true),
  /**
   * Outbound by default: the task drawer places outbound calls. An inbound browser test must ask for it.
   */
  direction: z.enum(['inbound', 'outbound']).default('outbound'),
  /**
   * How the call is placed:
   *   - `browser-test` (default): the operator talks to the agent from
   *     the browser (mic in, agent out). Same dev harness as before.
   *   - `sip-outbound`: the agent dials a real phone over SIP/Twilio
   *     and the operator monitors the live call listen-only.
   * Drives which session the API mints (full participant token vs
   * viewer token) and how the FE connects (`mode` in the response).
   * Optional — absent is treated as `browser-test`.
   */
  transport: z.enum(['browser-test', 'sip-outbound']).optional(),
  /**
   * SIP-only. Destination number to dial, E.164. When omitted the API
   * uses the patient's on-file number. The dialog pre-fills the patient
   * number but lets the operator override it (e.g. their own cell) so a
   * test call never accidentally rings a real patient. Ignored for
   * `browser-test`.
   */
  toPhoneOverride: e164Schema.optional(),
  realtimeProvider: realtimeProviderField,
  realtimeModel: realtimeModelField,
  /**
   * Caller-audio enhancement for this call only: `bvc` (Krisp telephony
   * background-voice cancellation, the default), `nc` (noise only), `none`.
   * Exists for the EXP-0024 F2 A/B — two calls to one number — so the
   * deployment default is never flipped for everyone else.
   */
  inputFilter: inputFilterModeSchema.nullable().optional(),
  /**
   * WHO the patient hears, and WHAT TECHNOLOGY carries it (§5).
   *
   * Two ids into the shared catalog, never provider values: a persona names a
   * CATALOG voice and an agent names the stack that realizes it. Sending
   * `marin` or a Cartesia UUID here would put a stack-dependent id in a
   * request that outlives the stack it was written for.
   *
   * Both optional, and omission is what every existing caller already sends —
   * the runtime falls back to the deploy-wide default exactly as before, so
   * adding these changes no call that does not set them.
   *
   * Per call, because an arm is only attributable when one axis moves and the
   * others are pinned, and that is a choice about THIS call rather than about
   * the deployment.
   */
  personaId: z.string().min(1).optional(),
  agentId: z.string().min(1).optional(),
  /**
   * Run only SOME of the v2 workflow — nodes turned off or stood in so a test
   * can reach one step without walking everything ahead of it (ADR-306).
   *
   * The API refuses a plan written for a workflow the call will not run,
   * because it would be silently ignored. Omitted means the full call, which
   * is what every existing caller sends.
   */
  testPlan: stepPlanSchema.optional(),
};

export const scheduleReferralRequestSchema = z
  .object({
    callType: z.literal('schedule_referral'),
    /** Task that initiated this call. Server resolves the task's
     *  primary document and creates the interaction_tasks link. */
    taskId: z.string().uuid().optional(),
    /** Explicit document override, for a caller that names the document
     *  directly. One of taskId or documentId must be set. */
    documentId: z.string().uuid().optional(),
    ...commonCallFields,
  })
  .refine((v) => !!(v.taskId || v.documentId), {
    message: 'schedule_referral requires either taskId or documentId',
    path: ['taskId'],
  });

export const generalInquiryRequestSchema = z.object({
  callType: z.literal('general_inquiry'),
  /** Optional task to file the call against (creates the
   *  interaction_tasks link without preloading a document). */
  taskId: z.string().uuid().optional(),
  ...commonCallFields,
});

/**
 * The complete `/api/browser-call/test` request body — discriminated by
 * `callType`. Adding a new outbound use case = one new variant added to
 * this union (and a matching branch on `BrowserCallService.startTest`'s
 * `buildCallReasonFor`).
 */
export const startCallBodySchema = z.discriminatedUnion('callType', [
  scheduleReferralRequestSchema,
  generalInquiryRequestSchema,
]);

export type StartCallBody = z.infer<typeof startCallBodySchema>;

/**
 * Response from `/api/browser-call/test` — LiveKit room creds plus the
 * persisted interaction id the FE uses to subscribe to the live
 * transcript and link to the post-call detail sheet.
 */
export const startCallResponseSchema = z.object({
  token: z.string().min(1),
  url: z.string().url(),
  roomName: z.string().min(1),
  interactionId: z.string().uuid(),
  /**
   * Connection role for the minted token:
   *   - `participant`: full publish+subscribe (browser-test — operator
   *     talks). The FE publishes the mic.
   *   - `viewer`: subscribe-only (sip-outbound — operator monitors). The
   *     FE must NOT publish audio; the viewer token can't anyway.
   * Defaults to `participant` so older payloads (pre-SIP) keep working.
   */
  mode: z.enum(['participant', 'viewer']).default('participant'),
});

export type StartCallResponse = z.infer<typeof startCallResponseSchema>;
